<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Import de données</h1>
        <p class="page-subtitle">Importez vos catégories, produits et clients depuis un fichier Excel</p>
      </div>
    </div>

    <!-- Success message -->
    <v-alert v-if="success" type="success" text dense dismissible class="mb-4">
      Les données ont été importées et enregistrées avec succès.
    </v-alert>

    <!-- Error message -->
    <v-alert v-if="error_occured" type="error" text dense dismissible class="mb-4">
      {{ error_message || "Une erreur s'est produite lors de l'enregistrement." }}
    </v-alert>

    <!-- STEP 1: Upload -->
    <div v-if="!hasData">
      <v-row>
        <v-col cols="12" md="7">
          <v-card flat class="import-card">
            <v-card-text class="pa-6">
              <div class="upload-zone" @click="$refs.fileInput.click()" @dragover.prevent @drop.prevent="handleDrop">
                <v-icon size="48" color="primary" class="mb-3">mdi-file-upload-outline</v-icon>
                <p class="upload-title">Glissez votre fichier Excel ici</p>
                <p class="upload-sub">ou cliquez pour parcourir</p>
                <p class="upload-hint">.xlsx uniquement — max 5 Mo</p>
              </div>
              <input
                type="file"
                ref="fileInput"
                style="display: none"
                accept=".xlsx,.xls"
                @change="handleFileSelect"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="5">
          <v-card flat class="import-card">
            <v-card-text class="pa-6">
              <h3 class="instructions-title">Comment ça marche</h3>

              <div class="step-item">
                <div class="step-num">1</div>
                <div>
                  <strong>Téléchargez le modèle</strong>
                  <p>Utilisez notre fichier modèle pour structurer vos données correctement.</p>
                  <v-btn small outlined color="primary" href="/data/import_template.xlsx" target="_blank">
                    <v-icon left small>mdi-download</v-icon>
                    Télécharger le modèle
                  </v-btn>
                </div>
              </div>

              <div class="step-item">
                <div class="step-num">2</div>
                <div>
                  <strong>Remplissez vos données</strong>
                  <p>Le fichier contient 3 onglets obligatoires :</p>
                  <ul class="sheet-list">
                    <li><strong>Categories</strong> — Nom</li>
                    <li><strong>Produits</strong> — Nom, Categorie, Cout, Prix, Prix minimum, Stock initial, Code barre</li>
                    <li><strong>Clients</strong> — Nom, Email, Telephone, Adresse, Reduction</li>
                  </ul>
                </div>
              </div>

              <div class="step-item">
                <div class="step-num">3</div>
                <div>
                  <strong>Importez et vérifiez</strong>
                  <p>Après l'import, vous pourrez vérifier et modifier les données avant de les enregistrer définitivement.</p>
                </div>
              </div>

              <v-alert type="warning" text dense class="mt-3 mb-0">
                <strong>Important :</strong> Ne modifiez pas le nom des colonnes ni l'ordre des onglets dans le fichier Excel.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Loading -->
    <PreloaderComponent :enable="enable" />

    <!-- STEP 2: Review & Edit -->
    <div v-if="hasData && enable">
      <!-- Action bar -->
      <v-card flat class="filter-card mb-4">
        <v-card-text class="py-3">
          <v-row align="center" dense>
            <v-col cols="auto">
              <v-chip small outlined color="primary" class="mr-2">
                {{ (formattedData.categories || []).length }} catégories
              </v-chip>
              <v-chip small outlined color="secondary" class="mr-2">
                {{ (formattedData.products || []).length }} produits
              </v-chip>
              <v-chip small outlined color="info">
                {{ (formattedData.customers || []).length }} clients
              </v-chip>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <div class="d-flex" style="gap: 8px;">
                <v-btn outlined small @click="clearData">
                  <v-icon left small>mdi-upload</v-icon>
                  Nouveau fichier
                </v-btn>
                <v-btn outlined small @click="refreshData">
                  <v-icon left small>mdi-refresh</v-icon>
                  Réinitialiser
                </v-btn>
                <v-btn color="primary" depressed small @click="saveData" :loading="saving">
                  <v-icon left small>mdi-content-save</v-icon>
                  Enregistrer
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Tabs -->
      <v-card flat class="table-card">
        <v-tabs v-model="tab" color="primary" class="import-tabs">
          <v-tab v-for="table in tables" :key="table.name">
            {{ table.label }}
            <v-chip x-small class="ml-2" color="primary" dark>{{ (formattedData[table.name] || []).length }}</v-chip>
          </v-tab>
        </v-tabs>

        <v-divider></v-divider>

        <v-tabs-items v-model="tab">
          <v-tab-item v-for="table in tables" :key="table.name">
            <!-- Add row button -->
            <div class="d-flex justify-end pa-3 pb-0">
              <v-btn x-small outlined color="primary" @click="addRow(table.name)">
                <v-icon left x-small>mdi-plus</v-icon>
                Ajouter
              </v-btn>
            </div>

            <!-- Data table -->
            <v-simple-table dense class="import-table">
              <thead>
                <tr>
                  <th v-for="header in table.headers.filter(h => h.value !== 'actions')" :key="header.value">
                    {{ header.text }}
                  </th>
                  <th style="width: 50px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, rowIndex) in (formattedData[table.name] || [])" :key="rowIndex">
                  <td
                    v-for="header in table.headers.filter(h => h.value !== 'actions')"
                    :key="header.value"
                    @dblclick="editCell(rowIndex, header.value, table.name)"
                    :class="{ 'cell-editing': isEditing(rowIndex, header.value, table.name) }"
                  >
                    <!-- Editing mode -->
                    <template v-if="isEditing(rowIndex, header.value, table.name)">
                      <v-select
                        v-if="table.name === 'products' && header.value === 'category'"
                        v-model="editableValue"
                        :items="categories"
                        @change="saveEdit(rowIndex, header.value, table.name)"
                        autofocus
                        dense
                        solo
                        flat
                        hide-details
                        class="cell-editor"
                      ></v-select>
                      <input
                        v-else
                        v-model="editableValue"
                        :type="header.type === 'number' ? 'number' : 'text'"
                        :step="header.type === 'number' ? 0.01 : null"
                        @blur="saveEdit(rowIndex, header.value, table.name)"
                        @keyup.enter="saveEdit(rowIndex, header.value, table.name)"
                        @keyup.esc="stopEditing"
                        autofocus
                        class="cell-editor-input"
                      />
                    </template>
                    <!-- Display mode -->
                    <template v-else>
                      <span :class="{ 'cell-empty': !item[header.value] && item[header.value] !== 0 }">
                        {{ item[header.value] || '—' }}
                      </span>
                    </template>
                  </td>
                  <td class="text-center">
                    <v-btn icon x-small @click="deleteRow(rowIndex, table.name)" color="error">
                      <v-icon x-small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr v-if="!(formattedData[table.name] || []).length">
                  <td :colspan="table.headers.length" class="text-center text--secondary pa-6">Aucune donnée</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>
  </div>
</template>

<script>
import readXlsxFile from 'read-excel-file'
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'ImportView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    tab: null,
    excelData: {},
    formattedData: {},
    file: null,
    enable: true,
    saving: false,
    success: false,
    error_occured: false,
    error_message: '',
    editingCell: { row: null, column: null, table: null },
    editableValue: '',
    lastValueOnEdit: '',
    items: [{ text: 'Import', disabled: false, href: '/import' }],
    tables: [
      {
        name: 'categories',
        label: 'Catégories',
        headers: [
          { text: 'Nom', value: 'name', type: 'text' },
          { text: '', value: 'actions', align: 'end' },
        ],
      },
      {
        name: 'products',
        label: 'Produits',
        headers: [
          { text: 'Nom', value: 'name', type: 'text' },
          { text: 'Catégorie', value: 'category', type: 'text' },
          { text: 'Coût', value: 'cost', type: 'number' },
          { text: 'Prix', value: 'price', type: 'number' },
          { text: 'Prix min.', value: 'min_price', type: 'number' },
          { text: 'Stock', value: 'stock', type: 'number' },
          { text: 'Code barre', value: 'barcode', type: 'text' },
          { text: '', value: 'actions', align: 'end' },
        ],
      },
      {
        name: 'customers',
        label: 'Clients',
        headers: [
          { text: 'Nom', value: 'full_name', type: 'text' },
          { text: 'Email', value: 'email', type: 'email' },
          { text: 'Téléphone', value: 'phone', type: 'text' },
          { text: 'Adresse', value: 'address', type: 'text' },
          { text: 'Réduction (%)', value: 'discount', type: 'number' },
          { text: '', value: 'actions', align: 'end' },
        ],
      },
    ],
  }),

  computed: {
    hasData() {
      return Object.keys(this.formattedData).length > 0
    },
    categories() {
      if (!this.formattedData.categories) return []
      return this.formattedData.categories.map(function (c) { return c.name })
    },
  },

  methods: {
    handleFileSelect(e) {
      if (e.target.files && e.target.files[0]) {
        this.processFile(e.target.files[0])
      }
    },

    handleDrop(e) {
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        this.processFile(e.dataTransfer.files[0])
      }
    },

    processFile(newFile) {
      var dataKeys = ['categories', 'products', 'customers']
      var sheetNames = ['Categories', 'Produits', 'Clients']
      var schemas = {
        Categories: { 'Nom': { prop: 'name', type: String, required: true } },
        Produits: {
          'Nom': { prop: 'name', type: String, required: true },
          'Categorie': { prop: 'category', type: String, required: true },
          'Cout': { prop: 'cost', type: Number, required: true },
          'Prix': { prop: 'price', type: Number, required: true },
          'Prix minimum': { prop: 'min_price', type: Number, required: false },
          'Stock initial': { prop: 'stock', type: Number, required: true },
          'Code barre': { prop: 'barcode', type: String, required: false },
        },
        Clients: {
          'Nom': { prop: 'full_name', type: String, required: true },
          'Email': { prop: 'email', type: String, required: false },
          'Telephone': { prop: 'phone', type: String, required: true },
          'Adresse': { prop: 'address', type: String, required: false },
          'Reduction': { prop: 'discount', type: Number, required: false },
        },
      }

      var self = this
      self.enable = false
      self.success = false
      self.error_occured = false

      readXlsxFile(newFile, { getSheets: true }).then(function (sheets) {
        if (sheets.length !== sheetNames.length) {
          self.enable = true
          alert('Le fichier doit contenir exactement 3 onglets : Categories, Produits, Clients.')
          return
        }

        var data = {}
        var sheetsProcessed = 0

        sheets.forEach(function (sheet) {
          var index = sheetNames.indexOf(sheet.name)
          if (index === -1) {
            self.enable = true
            alert('Onglet "' + sheet.name + '" non reconnu. Attendus : Categories, Produits, Clients.')
            return
          }

          readXlsxFile(newFile, { schema: schemas[sheet.name], sheet: index + 1 }).then(function (result) {
            if (result.errors.length) {
              self.enable = true
              alert('Erreur dans l\'onglet "' + sheet.name + '". Vérifiez les données et le format des colonnes.')
              return
            }

            data[dataKeys[index]] = result.rows
            sheetsProcessed++

            if (sheetsProcessed === sheetNames.length) {
              self.excelData = self.freshData(data)
              self.formattedData = self.freshData(data)
              self.enable = true
            }
          })
        })
      }).catch(function () {
        self.enable = true
        alert('Impossible de lire le fichier. Vérifiez qu\'il est bien au format .xlsx.')
      })
    },

    clearData() {
      this.formattedData = {}
      this.excelData = {}
      this.file = null
      // Reset file input
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    refreshData() {
      this.formattedData = this.freshData(this.excelData)
    },

    saveData() {
      var self = this
      self.saving = true
      self.success = false
      self.error_occured = false

      this.$axios.post('/data/import', this.getJsonData())
        .then(function (response) {
          if (response && response.status >= 200 && response.status < 300) {
            self.clearData()
            self.success = true
          } else {
            self.error_occured = true
            self.error_message = (response && response.data && response.data.message) || "Une erreur s'est produite."
          }
          self.saving = false
        })
        .catch(function (error) {
          self.error_occured = true
          self.error_message = (error.response && error.response.data && error.response.data.message) || "Erreur de connexion."
          self.saving = false
        })
    },

    editCell(row, column, table) {
      this.editingCell = { row: row, column: column, table: table }
      this.editableValue = this.formattedData[table][row][column]
      this.lastValueOnEdit = this.formattedData[table][row][column]
    },

    saveEdit(row, column, table) {
      var val = typeof this.editableValue === 'string' ? this.editableValue.trim() : this.editableValue
      this.formattedData[table][row][column] = val
      // Sync category name change to products
      if (table === 'categories' && column === 'name') {
        var products = this.formattedData.products || []
        for (var i = 0; i < products.length; i++) {
          if (products[i].category === this.lastValueOnEdit) {
            products[i].category = val
          }
        }
      }
      this.stopEditing()
    },

    stopEditing() {
      this.editingCell = { row: null, column: null, table: null }
    },

    isEditing(row, column, table) {
      return this.editingCell.row === row && this.editingCell.column === column && this.editingCell.table === table
    },

    addRow(table) {
      var newRow = {}
      var headers = this.tables.find(function (t) { return t.name === table }).headers
      headers.forEach(function (h) {
        if (h.value !== 'actions') {
          newRow[h.value] = h.type === 'number' ? 0 : ''
        }
      })
      if (!this.formattedData[table]) this.$set(this.formattedData, table, [])
      this.formattedData[table].unshift(newRow)
    },

    deleteRow(index, table) {
      var value = this.formattedData[table][index].name || this.formattedData[table][index].full_name
      // Clear category from products if deleting a category
      if (table === 'categories' && value) {
        var products = this.formattedData.products || []
        for (var i = 0; i < products.length; i++) {
          if (products[i].category === value) products[i].category = ''
        }
      }
      this.formattedData[table].splice(index, 1)
    },

    getJsonData() {
      return JSON.stringify(this.formattedData)
    },

    freshData(data) {
      return JSON.parse(JSON.stringify(data))
    },
  },
}
</script>

<style scoped>
/* Upload zone */
.import-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  height: 100%;
}

.upload-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 10px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f8fafc;
}

.upload-zone:hover {
  border-color: #194173;
  background: #eff6ff;
}

.upload-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a2332;
  margin: 0 0 4px;
}

.upload-sub {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0 0 8px;
}

.upload-hint {
  font-size: 0.6875rem;
  color: #94a3b8;
  margin: 0;
}

/* Instructions */
.instructions-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a2332;
  margin-bottom: 16px;
}

.step-item {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.step-item strong {
  font-size: 0.8125rem;
  color: #1a2332;
}

.step-item p {
  font-size: 0.75rem;
  color: #64748b;
  margin: 2px 0 6px;
}

.step-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #194173;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.sheet-list {
  font-size: 0.75rem;
  color: #475569;
  padding-left: 16px;
  margin: 0;
}

.sheet-list li { margin-bottom: 2px; }

/* Filter / action bar */
.filter-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
}

/* Table card */
.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  overflow: hidden;
}

.import-tabs {
  background: #f8fafc;
}

/* Import table */
.import-table thead th {
  font-size: 0.6875rem !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b !important;
  background: transparent !important;
  border-bottom: 1px solid #e8ecf1 !important;
  padding: 8px 12px !important;
}

.import-table tbody td {
  font-size: 0.8125rem;
  padding: 6px 12px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  cursor: pointer;
  min-height: 36px;
}

.import-table tbody td:hover {
  background: #f8fafc;
}

.cell-editing {
  padding: 2px 4px !important;
  background: #eff6ff !important;
}

.cell-editor-input {
  width: 100%;
  padding: 4px 8px;
  border: 2px solid #3b82f6;
  border-radius: 4px;
  font-size: 0.8125rem;
  outline: none;
  background: #fff;
}

.cell-empty {
  color: #cbd5e1;
}
</style>
